import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Question } from '../types';

interface QuestionCardProps {
  question: Question;
  selectedAnswer: number | null;
  onAnswerSelect: (index: number) => void;
  showResult: boolean;
  disabled?: boolean;
  opponentAnswer?: number | null; // Opponent's selected answer (for multiplayer)
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.4s ease-out;
`;

const QuestionText = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
  text-align: center;
  margin: 0;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1.25rem;
  }
`;

const CategoryBadge = styled.div`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 600;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const AnswersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  ${({ theme }) => theme.breakpoints.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
`;

const AnswerButton = styled(motion.button)<{
  $isSelected: boolean;
  $isCorrect?: boolean;
  $isWrong?: boolean;
  $showResult: boolean;
}>`
  padding: 16px 20px;
  min-height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 600;
  text-align: left;
  cursor: ${({ $showResult }) => ($showResult ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  border: 3px solid ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.primary : theme.colors.surface
  };
  background: ${({ theme, $isSelected, $isCorrect, $isWrong, $showResult }) => {
    if ($showResult) {
      if ($isCorrect) return theme.colors.success;
      if ($isWrong) return theme.colors.error;
    }
    return $isSelected ? theme.colors.surface : theme.colors.background;
  }};
  color: ${({ theme, $showResult, $isCorrect, $isWrong }) => {
    if ($showResult && ($isCorrect || $isWrong)) return '#fff';
    return theme.colors.text;
  }};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  animation: ${({ $isCorrect, $showResult }) =>
    $showResult && $isCorrect ? pulse : 'none'} 0.6s ease;
  animation: ${({ $isWrong, $showResult }) =>
    $showResult && $isWrong ? shake : 'none'} 0.5s ease;

  &:hover:not(:disabled) {
    transform: ${({ $showResult }) => ($showResult ? 'none' : 'translateY(-2px)')};
    border-color: ${({ theme, $showResult }) =>
      $showResult ? 'inherit' : theme.colors.secondary
    };
    box-shadow: ${({ theme, $showResult }) =>
      $showResult ? 'none' : theme.shadows.md
    };
  }

  &:active:not(:disabled) {
    transform: ${({ $showResult }) => ($showResult ? 'none' : 'scale(0.98)')};
  }

  &:disabled {
    opacity: ${({ $showResult }) => ($showResult ? 1 : 0.6)};
  }

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1rem;
    padding: 14px 18px;
    min-height: 56px;
  }
`;

const AnswerPrefix = styled.span`
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  line-height: 28px;
  font-size: 0.9rem;
  margin-right: ${({ theme }) => theme.spacing.sm};
  font-weight: 700;
`;

const OpponentBadge = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.info}40;
  color: ${({ theme }) => theme.colors.info};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: auto;
`;

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  selectedAnswer,
  onAnswerSelect,
  showResult,
  disabled,
  opponentAnswer,
}) => {
  const answerPrefixes = ['A', 'B', 'C', 'D'];

  return (
    <CardContainer>
      <div>
        <CategoryBadge>{question.category}</CategoryBadge>
        <QuestionText>{question.question}</QuestionText>
      </div>

      <AnswersGrid>
        {question.options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrect = showResult && index === question.correctAnswer;
          const isWrong = showResult && isSelected && index !== question.correctAnswer;
          const isOpponentChoice = opponentAnswer === index;

          return (
            <AnswerButton
              key={index}
              onClick={() => !showResult && !disabled && onAnswerSelect(index)}
              $isSelected={isSelected}
              $isCorrect={isCorrect}
              $isWrong={isWrong}
              $showResult={showResult}
              disabled={disabled || showResult}
              whileHover={!showResult && !disabled ? { scale: 1.02 } : {}}
              whileTap={!showResult && !disabled ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <AnswerPrefix>{answerPrefixes[index]}</AnswerPrefix>
              {option}
              {showResult && isOpponentChoice && !isSelected && (
                <OpponentBadge
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                >
                  👤 Opponent
                </OpponentBadge>
              )}
              {showResult && isCorrect && (
                <motion.span
                  style={{ marginLeft: isOpponentChoice && !isSelected ? '8px' : 'auto', fontSize: '1.5rem', fontWeight: '700' }}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 10 }}
                >
                  ✓
                </motion.span>
              )}
              {showResult && isWrong && (
                <motion.span
                  style={{ marginLeft: 'auto', fontSize: '1.5rem', fontWeight: '700' }}
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.4 }}
                >
                  ✗
                </motion.span>
              )}
            </AnswerButton>
          );
        })}
      </AnswersGrid>
    </CardContainer>
  );
};

export default QuestionCard;
